import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Camera, FirstAid, Warning, PencilSimple } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isAdmin, isParent, can, canFinance } from "@/lib/roles";
import { StudentAvatar } from "@/components/StudentAvatar";
import { PhotoUploader } from "@/components/PhotoUploader";
import { OverviewTab } from "@/pages/admin/student/OverviewTab";
import { AttendanceTab } from "@/pages/admin/student/AttendanceTab";
import { NotesTab } from "@/pages/admin/student/NotesTab";
import { ClassroomTab } from "@/pages/admin/student/ClassroomTab";
import { HealthTab } from "@/pages/admin/student/HealthTab";
import { FamilyTab } from "@/pages/admin/student/FamilyTab";
import { BillingTab } from "@/pages/admin/student/BillingTab";
import { FormsTab } from "@/pages/admin/student/FormsTab";
import { PhotosTab } from "@/pages/admin/student/PhotosTab";
import { HistoryTab } from "@/pages/admin/student/HistoryTab";
import { DetailsDialog } from "@/pages/admin/student/DetailsDialog";

const PERIODS = [["today", "Today"], ["week", "This week"], ["month", "This month"], ["billing", "Billing period"],
                 ["school_year", "School year"], ["custom", "Custom range"]];
const ATT = { in: ["Checked in", "bg-emerald-50 text-emerald-700"], out: ["Checked out", "bg-slate-100 text-slate-600"],
              absent: ["Absent", "bg-red-50 text-red-600"], none: ["Not arrived", "bg-amber-50 text-amber-700"] };

const TABS = [
  ["overview", "Overview"], ["attendance", "Attendance"], ["notes", "Development & Staff Notes"],
  ["classroom", "Classroom & Schedule"], ["health", "Health & Services"], ["family", "Family & Pickups"],
  ["billing", "Billing & Fees"], ["forms", "Forms & Documents"], ["photos", "Photos & Media"], ["history", "Activity History"],
];

export default function StudentProfile({ portal = false }) {
  const { studentId } = useParams();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [photoOpen, setPhotoOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const q = period === "custom" && range.start && range.end ? { period: "custom", ...range } : { period };
      const { data } = await api.get(`/students/${studentId}/profile`, { params: q });
      setData(data);
    } catch (e) { setError(apiError(e.response?.data?.detail)); }
  }, [studentId, period, range]);
  useEffect(() => { load(); }, [load]);

  const setTab = (t) => setParams({ tab: t });
  const visibleTabs = TABS.filter(([k]) => {
    if (k === "billing") return canFinance(user) || isParent(user) || user?.role === "auditor";
    if (k === "health") return can(user, "health", "view");
    if (k === "history") return !isParent(user);
    return true;
  });

  if (error) return <div className="text-[#EF4444] font-semibold" data-testid="profile-error">{error}</div>;
  if (!data) return <p className="text-muted-foreground" data-testid="profile-loading">Loading profile…</p>;
  const s = data.student;
  const back = portal ? "/portal" : "/admin/students";

  return (
    <div data-testid="student-profile">
      <Link to={back} className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-[#3B82F6] print:hidden" data-testid="back-to-students">
        <ArrowLeft size={16} /> {portal ? "My children" : "All students"}
      </Link>

      <div className="mt-5 bg-white rounded-3xl border border-border p-6 lg:p-8" data-testid="profile-header">
        <div className="flex flex-wrap gap-6 items-start">
          <div className="relative">
            <StudentAvatar student={s} size="xl" testid="profile-photo" />
            {(can(user, "media", "create") || isParent(user)) && (
              <button onClick={() => setPhotoOpen(true)} className="absolute -bottom-2 -right-2 h-9 w-9 rounded-full bg-[#3B82F6] text-white grid place-items-center shadow print:hidden"
                      data-testid="change-photo-btn" aria-label="Change profile photo"><Camera size={18} /></button>
            )}
          </div>
          <div className="flex-1 min-w-[16rem]">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-4xl font-bold tracking-tight" data-testid="profile-name">{s.name}</h1>
              {s.preferred_name && <span className="text-lg text-muted-foreground font-semibold">“{s.preferred_name}”</span>}
              {s.pronouns && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-muted">{s.pronouns}</span>}
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${ATT[s.today_state][1]}`} data-testid="profile-today-state">{ATT[s.today_state][0]}</span>
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#0F172A] text-white" data-testid="profile-status">{s.status}</span>
            </div>
            <dl className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
              {[["Student ID", s.student_number], ["Age", `${s.age || "—"}${s.birthday ? ` · ${s.birthday}` : ""}`],
                ["Classroom", `${s.room}${s.age_group ? ` · ${s.age_group}` : ""}`],
                ["Teachers", s.teachers.map((t) => t.name).join(", ") || "Not assigned"],
                ["Enrolled", s.enrollment_date || "—"], ["Transition", s.expected_transition_date || "—"],
                ["Guardians", s.guardians.map((g) => g.name || g.email).join(", ") || "—"],
                ["Profile complete", `${s.completion}%`]].map(([k, v]) => (
                <div key={k}><dt className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{k}</dt><dd className="font-semibold mt-0.5">{v}</dd></div>
              ))}
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              {s.alerts.medical && <span className="text-xs font-bold px-3 py-1 rounded-full bg-red-50 text-red-600" data-testid="alert-medical"><FirstAid size={12} className="inline mr-1" />Medical / allergy alert</span>}
              {s.alerts.pickup && <span className="text-xs font-bold px-3 py-1 rounded-full bg-orange-50 text-orange-700" data-testid="alert-pickup"><Warning size={12} className="inline mr-1" />Pickup restriction on file</span>}
              {data.billing && (
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${data.billing.past_due > 0 ? "bg-red-50 text-red-600" : data.billing.balance > 0 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="alert-balance">
                  {data.billing.balance > 0 ? `${money(data.billing.balance)} owed${data.billing.past_due > 0 ? ` · ${data.billing.days_past_due} days past due` : ""}` : "Account current"}
                </span>
              )}
              {s.scheduled_transfer && <span className="text-xs font-bold px-3 py-1 rounded-full bg-blue-50 text-blue-700">Moves to {s.scheduled_transfer.room} on {s.scheduled_transfer.effective_date}</span>}
            </div>
          </div>
          {!isParent(user) && can(user, "students", "edit") && (
            <Button variant="outline" onClick={() => setDetailsOpen(true)} className="rounded-full font-bold print:hidden" data-testid="edit-details-btn">
              <PencilSimple size={16} className="mr-2" /> Edit details</Button>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 items-end print:hidden">
        <div className="flex gap-1 flex-wrap" role="tablist" data-testid="profile-tabs">
          {visibleTabs.map(([k, label]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                    className={`px-4 py-2 rounded-full text-sm font-bold ${tab === k ? "bg-[#3B82F6] text-white" : "bg-white border border-border hover:border-blue-300"}`}
                    style={{ transition: "background-color .2s ease, border-color .2s ease" }}>{label}</button>
          ))}
        </div>
        {(tab === "overview" || tab === "attendance") && (
          <div className="ml-auto flex flex-wrap gap-2 items-end">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-10 rounded-xl w-44 bg-white" data-testid="period-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            {period === "custom" && (
              <>
                <div><Label className="text-xs font-bold">From</Label><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-10 rounded-xl w-40 bg-white" data-testid="range-start" /></div>
                <div><Label className="text-xs font-bold">To</Label><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-10 rounded-xl w-40 bg-white" data-testid="range-end" /></div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="mt-6" data-testid={`tab-panel-${tab}`}>
        {tab === "overview" && <OverviewTab data={data} user={user} />}
        {tab === "attendance" && <AttendanceTab student={s} period={period} range={range} user={user} />}
        {tab === "notes" && <NotesTab student={s} user={user} onChanged={load} />}
        {tab === "classroom" && <ClassroomTab student={s} user={user} onChanged={load} />}
        {tab === "health" && <HealthTab student={s} user={user} />}
        {tab === "family" && <FamilyTab student={s} user={user} onChanged={load} />}
        {tab === "billing" && <BillingTab student={s} billing={data.billing} user={user} onChanged={load} />}
        {tab === "forms" && <FormsTab student={s} data={data} user={user} />}
        {tab === "photos" && <PhotosTab student={s} user={user} onChanged={load} onUpload={() => setPhotoOpen(true)} />}
        {tab === "history" && <HistoryTab student={s} />}
      </div>

      <PhotoUploader open={photoOpen} onOpenChange={setPhotoOpen} studentId={s.id} hasCurrent={Boolean(s.photo_path)} onDone={load} />
      {isAdmin(user) || can(user, "students", "edit") ? (
        <DetailsDialog open={detailsOpen} onOpenChange={setDetailsOpen} student={s} onSaved={load} />
      ) : null}
    </div>
  );
}
